"use client";

import { useCallback, useEffect, useRef } from "react";
import { useListingBrowseContext } from "./context/listing-browse-context";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";
import { buildGA4Item } from "@/lib/analytics/ga4/items";
import {
  trackGA4SelectItem,
  trackGA4ViewItemList,
} from "@/lib/analytics/ga4/ecommerce";

const TRACKED_ITEM_LIMIT = 20;

function toGA4Item(product: BrowseListing, index?: number) {
  return buildGA4Item({
    itemId: product.publicIdentifier ?? product._id,
    itemName: product.title ?? "",
    priceCents: product.buyerUnitPriceCents,
    category: product.productType?.displayName,
    variant: product.condition,
    index,
  });
}

function useListName(): { id: string; name: string } {
  const { productType } = useListingBrowseContext();
  return {
    id: productType ?? "browse_results",
    name: productType ? `Browse ${productType}` : "Browse Results",
  };
}

function useViewItemListTracking(
  products: BrowseListing[],
  isLoading: boolean,
) {
  const list = useListName();
  const lastViewedList = useRef<string>("");

  useEffect(() => {
    if (isLoading || products.length === 0) return;
    const visible = products.slice(0, TRACKED_ITEM_LIMIT);
    const fingerprint = visible.map((product) => product._id).join(",");
    if (fingerprint === lastViewedList.current) return;
    lastViewedList.current = fingerprint;
    trackGA4ViewItemList(visible.map(toGA4Item), list.id, list.name);
  }, [products, isLoading, list.id, list.name]);
}

export function useListingResultTracking({
  products,
  isLoading,
}: {
  products: BrowseListing[];
  isLoading: boolean;
}) {
  const list = useListName();
  useViewItemListTracking(products, isLoading);

  return useCallback(
    (product: BrowseListing) =>
      trackGA4SelectItem(toGA4Item(product), list.id, list.name),
    [list.id, list.name],
  );
}
